import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    if (attempt > 8) {
      setStatus("timeout");
      return;
    }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") {
        setStatus("paid");
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (_e) {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => {
    poll();
  }, [poll]);

  return (
    <div data-testid="checkout-success-page" className="px-6 md:px-12 lg:px-24 py-32 max-w-2xl mx-auto text-center">
      <p className="label-eyebrow text-[#1E3A8A]">— Checkout</p>
      {status === "checking" && (
        <>
          <h1 className="font-serif-display text-5xl tracking-tighter font-light mt-4">
            Confirming your acquisition…
          </h1>
          <p className="text-[#555] mt-6 font-mono-aam text-xs">Polling Stripe · {sessionId?.slice(0, 16)}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <h1 className="font-serif-display text-6xl tracking-tighter font-light mt-4">
            <span className="italic text-[#1E3A8A]">Thank you.</span>
          </h1>
          <p className="text-[#333] mt-6 text-lg font-light">
            Your work is on its way. The artist has been notified, and you'll receive shipping details
            by email within 48 hours.
          </p>
          {info && (
            <p className="font-mono-aam text-sm text-[#555] mt-6">
              ${(info.amount_total / 100).toFixed(2)} {info.currency?.toUpperCase()} · paid
            </p>
          )}
          <div className="mt-12 flex justify-center gap-4">
            <Link
              to="/dashboard"
              data-testid="success-dashboard-link"
              className="label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
            >
              View Acquisitions
            </Link>
            <Link
              to="/gallery"
              className="label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
            >
              Continue Browsing
            </Link>
          </div>
        </>
      )}
      {(status === "error" || status === "expired" || status === "timeout") && (
        <>
          <h1 className="font-serif-display text-5xl tracking-tighter font-light mt-4">
            Something went wrong.
          </h1>
          <p className="text-[#555] mt-6">We couldn't confirm your payment. Please try again or contact us.</p>
          <Link
            to="/gallery"
            className="inline-block mt-10 label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
          >
            Back to Gallery
          </Link>
        </>
      )}
    </div>
  );
}
